import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { usePortal } from '@/contexts/PortalContext'
import { useAuthStore } from '@/stores/authStore'
import { useCartStore } from '@/stores/cartStore'
import { ShoppingCart, User, LogOut, Menu, X } from 'lucide-react'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/services/api'
import PortalNewsletterSignup from '@/components/PortalNewsletterSignup'
import { onDark } from '@/utils/brandColor'

// Every legal document is published on every white-label site — a customer buying through a
// reseller must be able to read the terms they are agreeing to on the site they are buying
// from. The documents stay Silux's, because Silux is the contracting party; what changes is
// the line above them naming the reseller as a representative partner. Listing the paths
// here rather than editing nine pages means a new legal page is covered automatically.
const LEGAL_PATHS = new Set([
  '/terms', '/privacy', '/cookie-policy', '/acceptable-use', '/website-terms',
  '/complaints-code', '/early-termination', '/repairs-replacements', '/free-sim-terms',
])

export default function PortalLayout() {
  const { portal, subdomain } = usePortal()
  const location = useLocation()
  const { isAuthenticated, user, logout } = useAuthStore()
  const { getItemCount } = useCartStore()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)

  // Category nav built from the portal's OWN products — empty categories are omitted,
  // and the whole shop is hidden when the tenant has shop_enabled = false.
  const { data: navData } = useQuery({
    queryKey: ['portalNavCategories', subdomain],
    queryFn: () => api.get(`/portal/nav-categories?subdomain=${subdomain}`).then(r => r.data),
    enabled: !!subdomain,
  })
  const shopEnabled = navData?.shop_enabled ?? true
  const categories: Array<{ label: string; path: string }> = navData?.categories || []

  const primaryColor = portal?.primary_color || '#0ea5e9'
  // The footer is near-black. A dark brand colour laid straight onto it (Lyra's purple)
  // renders headings that cannot be read, so lift the colour for use down there only —
  // the header, on white, keeps the exact brand hex.
  const footerHeadingColor = onDark(primaryColor)
  const brandName = portal?.trading_name || portal?.company_name || 'Portal'

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo / Brand */}
          <Link to="/" className="flex items-center gap-3">
            {portal?.logo_url ? (
              <img src={portal.logo_url} alt={brandName}
                   className="h-11 sm:h-12 w-auto max-w-[220px] sm:max-w-[280px] object-contain" />
            ) : (
              <span className="text-xl font-bold" style={{ color: primaryColor }}>
                {brandName}
              </span>
            )}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {/* Standalone "Shop" link intentionally hidden — browse via the SIM/Broadband category links. */}
            {shopEnabled && categories.map(c => (
              <Link key={c.path} to={c.path} className="text-gray-700 hover:text-gray-900 font-medium text-sm">{c.label}</Link>
            ))}
            {portal?.contact_phone && (
              <a href={`tel:${portal.contact_phone}`} className="text-gray-500 text-sm">{portal.contact_phone}</a>
            )}
            {shopEnabled && <Link to="/cart" className="relative text-gray-700">
              <ShoppingCart className="w-5 h-5" />
              {getItemCount() > 0 && (
                <span className="absolute -top-2 -right-2 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                  {getItemCount()}
                </span>
              )}
            </Link>}
            {isAuthenticated ? (
              <>
                <Link to="/account" className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900">
                  <User className="w-4 h-4" />
                  {user?.first_name}
                </Link>
                <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm text-gray-700 hover:text-gray-900 font-medium">Login</Link>
                <Link
                  to="/register"
                  className="text-sm text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: primaryColor }}
                >
                  Register
                </Link>
              </>
            )}
          </nav>

          {/* Mobile toggle */}
          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-3">
            {/* Standalone "Shop" link intentionally hidden — browse via the SIM/Broadband category links. */}
            {shopEnabled && categories.map(c => (
              <Link key={c.path} to={c.path} onClick={() => setMobileOpen(false)} className="block py-2 text-gray-700 font-medium">{c.label}</Link>
            ))}
            {shopEnabled && <Link to="/cart" onClick={() => setMobileOpen(false)} className="block py-2 text-gray-700 font-medium">Cart ({getItemCount()})</Link>}
            {isAuthenticated ? (
              <>
                <Link to="/account" onClick={() => setMobileOpen(false)} className="block py-2 text-gray-700 font-medium">My Account</Link>
                <button onClick={() => { handleLogout(); setMobileOpen(false) }} className="block py-2 text-red-600 font-medium">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="block py-2 text-gray-700 font-medium">Login</Link>
                <Link to="/register" onClick={() => setMobileOpen(false)} className="block py-2 text-white font-semibold text-center rounded-lg" style={{ backgroundColor: primaryColor }}>Register</Link>
              </>
            )}
          </div>
        )}
      </header>

      {/* Main */}
      <main>
        {LEGAL_PATHS.has(location.pathname.replace(/\/$/, '')) && (
          <div className="border-b" style={{ backgroundColor: `${primaryColor}0d`, borderColor: `${primaryColor}33` }}>
            <div className="max-w-4xl mx-auto px-4 py-3 text-sm text-gray-700">
              <strong>{brandName}</strong> is a representative partner of Silux Mobile. The services
              described in this document are provided by Silux Mobile, and these terms apply to the
              service you buy through {brandName}.
            </div>
          </div>
        )}
        <Outlet />
      </main>

      {/* Footer — mirrors the main site's four columns (Products / Support / Legal /
          Account) so a reseller's customer finds the same things in the same places. Only
          Silux-specific marketing (Special Offers, Reseller Program, About Us) is left out;
          every legal document IS published here, because a customer must be able to read the
          terms they are agreeing to on the site they are buying from. */}
      <footer className="mt-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Its own card, ABOVE the link columns. Appended after them it sat under the
              Legal list and read as part of that column instead of a section. */}
          <PortalNewsletterSignup />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
            {shopEnabled && (
              <div>
                <h3 className="text-base font-bold mb-4" style={{ color: footerHeadingColor }}>Products</h3>
                <ul className="space-y-2 text-gray-300">
                  {(categories.length ? categories : [
                    { label: 'SIM Only', path: '/sim-only' },
                    { label: 'Broadband', path: '/broadband' },
                    { label: 'Bundles', path: '/bundles' },
                  ]).map(c => (
                    <li key={c.path}><Link to={c.path} className="hover:text-white transition-colors">{c.label}</Link></li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <h3 className="text-base font-bold mb-4" style={{ color: footerHeadingColor }}>Support</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to="/network-coverage" className="hover:text-white transition-colors">Network Coverage</Link></li>
                {/* No "Support Tickets" link. Support on a white-label site is the
                    reseller's own — answering a ticket ourselves would disclose Silux Mobile
                    behind their brand, so portal customers are pointed at the reseller's
                    contact details below instead. The ticket system stays main-site only. */}
                <li><Link to="/repairs-replacements" className="hover:text-white transition-colors">Repairs &amp; Replacements</Link></li>
                {portal?.contact_email && (
                  <li><a href={`mailto:${portal.contact_email}`} className="hover:text-white transition-colors">Email Us</a></li>
                )}
                {portal?.contact_phone && (
                  <li><a href={`tel:${portal.contact_phone}`} className="hover:text-white transition-colors">{portal.contact_phone}</a></li>
                )}
              </ul>
            </div>

            <div>
              <h3 className="text-base font-bold mb-4" style={{ color: footerHeadingColor }}>Legal</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/acceptable-use" className="hover:text-white transition-colors">Acceptable Use Policy</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</Link></li>
                <li><Link to="/website-terms" className="hover:text-white transition-colors">Website Terms of Use</Link></li>
                <li><Link to="/complaints-code" className="hover:text-white transition-colors">Complaints Code of Practice</Link></li>
                <li><Link to="/early-termination" className="hover:text-white transition-colors">Early Termination Charges</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-bold mb-4" style={{ color: footerHeadingColor }}>Account</h3>
              <ul className="space-y-2 text-gray-300">
                {isAuthenticated ? (
                  <>
                    <li><Link to="/account" className="hover:text-white transition-colors">My Account</Link></li>
                    <li><Link to="/account/orders" className="hover:text-white transition-colors">My Orders</Link></li>
                    <li><Link to="/account/invoices" className="hover:text-white transition-colors">Invoices</Link></li>
                    <li><Link to="/account/topup" className="hover:text-white transition-colors">Top Up</Link></li>
                  </>
                ) : (
                  <>
                    <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
                    <li><Link to="/register" className="hover:text-white transition-colors">Create Account</Link></li>
                  </>
                )}
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-start gap-4">
            <div className="flex items-center gap-3">
              {portal?.logo_url
                ? <img src={portal.logo_url} alt={brandName}
                       className="h-10 w-auto max-w-[220px] object-contain" />
                : <span className="font-bold text-lg" style={{ color: footerHeadingColor }}>{brandName}</span>}
              {portal?.tagline && <span className="text-sm text-gray-400">{portal.tagline}</span>}
            </div>
            <div className="text-xs text-gray-400 sm:text-right space-y-1">
              <p>{portal?.footer_text || `© ${new Date().getFullYear()} ${brandName}. All rights reserved.`}</p>
              {/* The "representative partner of Silux Mobile" disclosure is deliberately NOT
                  here. It belongs on the legal documents — where the contracting party has to
                  be named — not on every page of a reseller's storefront, which is the point
                  of white-labelling it. The banner above <Outlet /> still carries it on each
                  LEGAL_PATHS page. */}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
